// PDF im Layout des Kraiss & Friz „Kunden-Bestellformular" (Word-Vorlage
// "Schweißgas Bestellformular"): Lieferanten-Kopf, Kundenblock, Adressen,
// komplette Artikelliste mit Bestellmengen, freie Zeilen, Leergut, Lieferturnus.
// Vektor-PDF über jsPDF (lazy geladen) → klein, scharf, als Mail-Anhang geeignet.

import type { GasEinstellungen, GasPosition } from './api';

export type GasPdfDaten = {
  bestelldatum: string; wunschtermin: string; bestelltext: string;
  ansprechpartner: string; email: string; telefon: string;
  rechnungsadresse: string; lieferadresse: string;
  positionen: GasPosition[]; leergut: string;
};

export function datumDE(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || '');
}

// Farben/Maße wie in der Word-Vorlage von Kraiss & Friz
const BLAU_HELL: [number, number, number] = [219, 229, 241];   // Kopfzellen
const GRAU_HELL: [number, number, number] = [242, 242, 242];   // Artikelspalten
const TITEL: [number, number, number] = [132, 150, 176];       // „Kunden-Bestellformular“
const LINK: [number, number, number] = [0, 0, 238];

// PDF-Bausteine nachladen. Nach einem Deploy fehlen die alten Dateien eines
// schon offenen Tabs – Vite lädt dann neu und liefert undefined; deshalb prüfen
// und eine verständliche Meldung geben. gasPdfVorladen() beim Öffnen der Seite
// sorgt dafür, dass die Bausteine schon da sind, bevor ein Deploy dazwischenkommt.
async function bausteine() {
  const [pdfModul, logoModul] = await Promise.all([import('jspdf'), import('./kraissFrizLogo')]);
  if (!pdfModul?.jsPDF || !logoModul?.KF_LOGO_PNG_B64) {
    throw new Error('Die Seite wurde inzwischen aktualisiert. Bitte mit Strg+F5 neu laden und den PDF-Knopf erneut drücken – die Bestellung ist gespeichert.');
  }
  return { jsPDF: pdfModul.jsPDF, KF_LOGO_PNG_B64: logoModul.KF_LOGO_PNG_B64, KF_LOGO_SEITENVERHAELTNIS: logoModul.KF_LOGO_SEITENVERHAELTNIS };
}
export function gasPdfVorladen() { bausteine().catch(() => { /* beim Erzeugen erneut versucht */ }); }

export async function gasBestellPdf(e: GasEinstellungen, d: GasPdfDaten) {
  const { jsPDF, KF_LOGO_PNG_B64, KF_LOGO_SEITENVERHAELTNIS } = await bausteine();
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const L = 17, R = 193, W = R - L;
  pdf.setLineWidth(0.25);
  pdf.setDrawColor(0);

  type Opt = { size?: number; bold?: boolean; align?: 'left' | 'right' | 'center'; farbe?: [number, number, number]; font?: string };
  const text = (t: string, x: number, y: number, o: Opt = {}) => {
    pdf.setFont(o.font ?? 'helvetica', o.bold ? 'bold' : 'normal');
    pdf.setFontSize(o.size ?? 10.5);
    pdf.setTextColor(...(o.farbe ?? [0, 0, 0]));
    pdf.text(t, x, y, { align: o.align ?? 'left' });
  };
  const breite = (t: string, size: number, bold = false) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(size);
    return pdf.getTextWidth(t);
  };
  // Zelle: Rahmen, optional Füllung; Text vertikal mittig
  const zelle = (x: number, y: number, w: number, h: number, t: string, o: Opt & { fill?: [number, number, number] } = {}) => {
    if (o.fill) { pdf.setFillColor(...o.fill); pdf.rect(x, y, w, h, 'FD'); } else pdf.rect(x, y, w, h);
    if (!t) return;
    const size = o.size ?? 10.5;
    const ty = y + h / 2 + size * 0.125;
    const tx = o.align === 'center' ? x + w / 2 : o.align === 'right' ? x + w - 1.8 : x + 1.8;
    text(t, tx, ty, { ...o, size });
  };
  // „Label: Wert“ in einer Zelle (Label fett)
  const labelZelle = (x: number, y: number, w: number, h: number, label: string, wert: string) => {
    pdf.rect(x, y, w, h);
    const ty = y + h / 2 + 10.5 * 0.125;
    text(label, x + 1.8, ty, { bold: true });
    text(wert, x + 1.8 + breite(label + ' ', 10.5, true), ty);
  };

  const lf = e.lieferant;

  // ── Titel ──
  text('Kunden-Bestellformular', L + 2, 23, { font: 'times', size: 30, farbe: TITEL });
  pdf.setDrawColor(...TITEL); pdf.setLineWidth(0.4);
  pdf.line(L + 2, 25.5, R - 1, 25.5);
  pdf.setDrawColor(0); pdf.setLineWidth(0.25);

  // ── Lieferanten-Kasten mit Logo ──
  let y = 28;
  const boxH = 45;
  pdf.rect(L, y, W, boxH);
  text(lf.abteilung, L + 2.5, y + 7, { bold: true, size: 12 });
  text(lf.strasse, L + 2.5, y + 14);
  text(lf.ort, L + 2.5, y + 20.5);
  text(`Telefon: ${lf.telefon}`, L + 2.5, y + 27);
  text(`Telefax: ${lf.telefax}`, L + 2.5, y + 34, { bold: true, size: 12.5 });
  text('E-Mail:', L + 2.5, y + 41.5, { bold: true, size: 12.5 });
  const mx = L + 2.5 + breite('E-Mail: ', 12.5, true);
  text(lf.email, mx, y + 41.5, { bold: true, size: 12.5, farbe: LINK });
  pdf.setDrawColor(...LINK); pdf.line(mx, y + 42.3, mx + breite(lf.email, 12.5, true), y + 42.3); pdf.setDrawColor(0);
  const logoW = 97, logoH = logoW / KF_LOGO_SEITENVERHAELTNIS;
  pdf.addImage('data:image/png;base64,' + KF_LOGO_PNG_B64, 'PNG', R - logoW - 4, y + (boxH - logoH) / 2 - 2, logoW, logoH);

  // ── Kundenblock ──
  y += boxH + 9;
  const SP = L + W * 0.4525;          // Spaltengrenze wie im Formular
  const wl = SP - L, wr = R - SP;
  const zh = 5.8;
  zelle(L, y, wl, 8.5, `Kd-Nr: ${lf.kundennr}`, { fill: BLAU_HELL, bold: true, size: 15 });
  zelle(SP, y, wr, 8.5, e.besteller.firma, { fill: BLAU_HELL, bold: true, size: 15, align: 'center' });
  y += 8.5;
  labelZelle(L, y, wl, zh, 'Ansprechpartner:', d.ansprechpartner);
  labelZelle(SP, y, wr, zh, 'E-Mail:', d.email);
  y += zh;
  labelZelle(L, y, wl, zh, 'Telefon-Nr:', d.telefon);
  labelZelle(SP, y, wr, zh, 'Bestelldatum:', datumDE(d.bestelldatum));
  y += zh;
  zelle(L, y, wl, zh, 'Rechnungsadresse:', { fill: BLAU_HELL, bold: true, align: 'center' });
  zelle(SP, y, wr, zh, 'Lieferadresse:', { fill: BLAU_HELL, bold: true, align: 'center' });
  y += zh;
  const zeilen = (t: string) => (t || '').split(/\n/).map((z) => z.trim()).filter(Boolean);
  const ra = zeilen(d.rechnungsadresse), la = zeilen(d.lieferadresse);
  for (let i = 0; i < 5; i++) {
    zelle(L, y, wl, zh, ra[i] ?? '');
    zelle(SP, y, wr, zh, la[i] ?? '');
    y += zh;
  }
  zelle(L, y, wl, 7.5, 'Bestelltext/-Nr.:', { fill: BLAU_HELL, bold: true, size: 14, align: 'center' });
  zelle(SP, y, wr, 7.5, 'Wunschliefertermin:', { fill: BLAU_HELL, bold: true, size: 14, align: 'center' });
  y += 7.5;
  zelle(L, y, wl, zh, d.bestelltext, { align: 'center' });
  zelle(SP, y, wr, zh, datumDE(d.wunschtermin), { align: 'center' });
  y += zh + 10;

  // ── Artikeltabelle ──
  const NR = L, ART = L + 33.2, GR = L + 101.5, ME = L + 132.8;
  const kopf = () => {
    zelle(NR, y, ART - NR, 7.5, 'Artikel-Nr', { fill: BLAU_HELL, bold: true, size: 14, align: 'center' });
    zelle(ART, y, GR - ART, 7.5, 'Artikel', { fill: BLAU_HELL, bold: true, size: 14, align: 'center' });
    zelle(GR, y, ME - GR, 7.5, 'Größe/Typ', { fill: BLAU_HELL, bold: true, size: 14, align: 'center' });
    zelle(ME, y, R - ME, 7.5, 'Bestellmenge', { fill: BLAU_HELL, bold: true, size: 14, align: 'center' });
    y += 7.5;
  };
  kopf();
  const rh = 6.1;
  const zeile = (p: GasPosition) => {
    if (y + rh > 284) { pdf.addPage(); y = 15; kopf(); }
    const artikel = (pdf.setFontSize(12).splitTextToSize(p.artikel, GR - ART - 3) as string[])[0] ?? '';
    zelle(NR, y, ART - NR, rh, p.artnr, { fill: GRAU_HELL, size: 12, align: 'center' });
    zelle(ART, y, GR - ART, rh, artikel, { fill: GRAU_HELL, size: 12 });
    zelle(GR, y, ME - GR, rh, p.groesse, { fill: GRAU_HELL, size: 12, align: 'center' });
    const m = (p.menge || '').trim();
    zelle(ME, y, R - ME, rh, m && m !== '0' ? m : '', { size: 12.5, bold: true, align: 'center' });
    y += rh;
  };
  const mengeVon = new Map(d.positionen.filter((p) => !p.frei).map((p) => [`${p.artnr}|${p.artikel}`, p.menge]));
  e.artikel.forEach((a) => zeile({ ...a, menge: mengeVon.get(`${a.artnr}|${a.artikel}`) ?? '' }));
  // Positionen, die nicht (mehr) in der Artikelliste stehen, und freie Zeilen
  const bekannt = new Set(e.artikel.map((a) => `${a.artnr}|${a.artikel}`));
  const extra = d.positionen.filter((p) => p.frei || !bekannt.has(`${p.artnr}|${p.artikel}`));
  extra.forEach(zeile);
  for (let i = extra.length; i < 1; i++) zeile({ artnr: '', artikel: '', groesse: '', menge: '' }); // Leerzeile wie im Formular

  // Leergut
  if (y + rh > 284) { pdf.addPage(); y = 15; }
  // Leergut-Text („13x Schweißgas, 3 Bündel, 24x Treibgas“) bei Bedarf zweizeilig wie im Formular
  const lg = (d.leergut || '').trim();
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  const lgZeilen = lg && lg !== '0' ? (pdf.splitTextToSize(lg, R - ME - 3) as string[]) : [];
  const lh = Math.max(rh, lgZeilen.length * 4.4 + 2);
  zelle(NR, y, ME - NR, lh, 'Leergutrücknahme (Gesamtmenge)', { fill: GRAU_HELL, bold: true, size: 12 });
  pdf.rect(ME, y, R - ME, lh);
  lgZeilen.forEach((z, i) => text(z, ME + (R - ME) / 2, y + lh / 2 - ((lgZeilen.length - 1) * 4.4) / 2 + 1.3 + i * 4.4, { size: 10, bold: true, align: 'center' }));
  y += lh;

  // ── Fuß ──
  if (y + 8 > 288) { pdf.addPage(); y = 15; }
  text(`Lieferturnus: ${lf.lieferturnus}`, L, y + 7, { size: 10.5 });
  return pdf;
}
